/**
 * service-franchise client. Mirrors the Phase-2 NestJS surface under
 * `/v1/franchise/...`:
 *
 *   /v1/franchise/franchises          — apply / submit / lifecycle / list / detail / tree
 *   /v1/franchise/franchises/applications/:id/review
 *   /v1/franchise/agreements          — create / sign / renew / list-by-franchise / detail
 *   /v1/franchise/tiers               — list / get
 *   /v1/franchise/dashboard           — central map / central revenue / alerts / per-franchisee
 *   /v1/franchise/revenue             — record / reconcile / dispute / settle / list / aggregate
 *   /v1/franchise/settlements         — run / get / list-for-franchise
 *   /v1/franchise/inventory           — allocate / consume / return / replenish / list / get
 *   /v1/franchise/bulletins           — publish / acknowledge / list-all / list-for-franchise
 *
 * Every endpoint is RBAC-protected (`Franchise/*`, `FranchiseAgreement/*`,
 * `FranchiseRevenue/*`, `FranchiseSettlement/*`, `FranchiseInventory/*`,
 * `FranchiseBulletin/*`, `FranchiseDashboard/read`). The dev token shim
 * won't always carry these — pages catch 401/403 and surface a permission
 * notice via DeferredNotice.
 *
 * Phase-4a wired onboarding + ops side. Phase-4b adds the remaining 13
 * screens (settlement tracking, remittance, revenue share, incentives,
 * performance, reconciliation, suspension, inventory, broadcasts, training,
 * transaction log, discrepancy, national map). For deferred surfaces (e.g.
 * dedicated incentive endpoint, cross-service transaction log) pages render
 * the UI shell + DeferredNotice quoting the missing path.
 *
 * Service-catalogue, device provisioning and certification tracking endpoints
 * do NOT exist server-side today. The pages render the UI shell and surface a
 * DeferredNotice for the missing surface.
 */
import { env } from '../env';

import { request } from './client';

const baseUrl = (): string => env.serviceFranchiseBaseUrl;

// ─── Types ────────────────────────────────────────────────────────────────

export type TierCode = 'TIER_1' | 'TIER_2' | 'TIER_3';

export type FranchiseStatus =
  | 'DRAFT'
  | 'APPLIED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'TRAINING'
  | 'LIVE'
  | 'SUSPENDED'
  | 'TERMINATED';

export type ApplicationStatus = 'PENDING' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

export type AgreementStatus = 'DRAFT' | 'ACTIVE' | 'EXPIRED' | 'TERMINATED';

export interface FranchiseTier {
  readonly id: string;
  readonly code: TierCode;
  readonly displayName: string;
  readonly description: string;
  readonly allowedServices: readonly string[];
  readonly defaultPermissions: readonly string[];
  readonly dailyTxnLimit: string;
  readonly deviceProfile: unknown;
  readonly active: boolean;
}

export interface ListTiersResponse {
  readonly items: readonly FranchiseTier[];
}

export interface Franchise {
  readonly id: string;
  readonly franchiseNumber: string;
  readonly tenantId: string;
  readonly operatorId: number;
  readonly parentId: string | null;
  readonly tierCode: TierCode;
  readonly legalName: string;
  readonly tradingName: string | null;
  readonly state: string;
  readonly lga: string;
  readonly town: string | null;
  readonly address: unknown;
  readonly geoCountryId: number | null;
  readonly geoCountryName: string | null;
  readonly geoStateId: number | null;
  readonly geoStateName: string | null;
  readonly geoLgaId: number | null;
  readonly geoLgaName: string | null;
  readonly geoWardId: number | null;
  readonly geoWardName: string | null;
  readonly status: FranchiseStatus;
  readonly ownerCustomerId: string;
  readonly payoutBankCode: string | null;
  readonly payoutAccountNumber: string | null;
  readonly commissionRate: string;
  readonly appliedAt: string | null;
  readonly approvedAt: string | null;
  readonly liveAt: string | null;
  readonly suspendedAt: string | null;
  readonly terminatedAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListFranchisesResponse {
  readonly items: readonly Franchise[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface FranchiseTreeNode {
  readonly franchise: Franchise;
  readonly ancestors: readonly Franchise[];
  readonly descendants: readonly Franchise[];
}

export interface FranchiseApplication {
  readonly id: string;
  readonly franchiseId: string;
  readonly requestedTier: TierCode;
  readonly formData: unknown;
  readonly status: ApplicationStatus;
  readonly checks: unknown;
  readonly decisionNote: string | null;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly submittedAt: string;
}

export interface FranchiseAgreement {
  readonly id: string;
  readonly franchiseId: string;
  readonly agreementNumber: string;
  readonly terms: unknown;
  readonly effectiveFrom: string;
  readonly effectiveUntil: string;
  readonly status: AgreementStatus;
  readonly signedBy: string | null;
  readonly signedAt: string | null;
  readonly counterSignedBy?: string | null;
  readonly counterSignedAt?: string | null;
  readonly pdfUrl?: string | null;
  readonly pdfUploadedAt?: string | null;
}

export interface CentralMapView {
  readonly byState: readonly { state: string; count: number }[];
  readonly byTier: readonly { tier: string; count: number }[];
  readonly byStatus: readonly { status: string; count: number }[];
  readonly totalLive: number;
}

export interface CentralRevenueDashboard {
  readonly fromDate: string;
  readonly toDate: string;
  readonly totalRevenue: string;
  readonly totalCommission: string;
  readonly snapshotCount: number;
  readonly topFranchises: readonly {
    readonly franchiseId: string;
    readonly revenue: string;
    readonly commission: string;
  }[];
}

export interface FranchiseAlert {
  readonly kind: string;
  readonly franchiseId: string;
  readonly detail: string;
  readonly severity: string;
}

export interface AlertsAnomaliesResponse {
  readonly alerts: readonly FranchiseAlert[];
}

export interface FranchiseeDashboard {
  readonly franchise: Franchise;
  readonly lastSnapshotDate: string | null;
  readonly last30DayRevenue: string;
  readonly last30DayCommission: string;
  readonly openInventoryAlerts: number;
  readonly unreadBulletins: number;
  readonly descendantCount: number;
}

// ─── Tiers ────────────────────────────────────────────────────────────────

export async function listTiers(query: { active?: boolean } = {}): Promise<ListTiersResponse> {
  return request<ListTiersResponse>(baseUrl(), '/v1/franchise/tiers', {
    method: 'GET',
    query: { active: query.active },
  });
}

export async function getTier(code: string): Promise<FranchiseTier> {
  return request<FranchiseTier>(baseUrl(), `/v1/franchise/tiers/${encodeURIComponent(code)}`, {
    method: 'GET',
  });
}

// ─── Franchises ───────────────────────────────────────────────────────────

export interface ListFranchisesQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly status?: FranchiseStatus;
  readonly tierCode?: TierCode;
  readonly state?: string;
  readonly parentId?: string;
}

export async function listFranchises(
  query: ListFranchisesQuery = {},
): Promise<ListFranchisesResponse> {
  return request<ListFranchisesResponse>(baseUrl(), '/v1/franchise/franchises', {
    method: 'GET',
    query: {
      page: query.page,
      pageSize: query.pageSize,
      status: query.status,
      tierCode: query.tierCode,
      state: query.state,
      parentId: query.parentId,
    },
  });
}

export async function getFranchise(id: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

export async function getFranchiseTree(id: string): Promise<FranchiseTreeNode> {
  return request<FranchiseTreeNode>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(id)}/tree`,
    { method: 'GET' },
  );
}

export interface ApplyFranchiseDto {
  readonly legalName: string;
  readonly tradingName?: string;
  readonly state: string;
  readonly lga: string;
  readonly town?: string;
  readonly address: Record<string, unknown>;
  readonly ownerCustomerId: string;
  readonly requestedTier: TierCode;
  readonly parentId?: string;
  readonly payoutBankCode?: string;
  readonly payoutAccountNumber?: string;
  readonly formData?: Record<string, unknown>;
  readonly operatorId: number;
  readonly geoCountryId?: number;
  readonly geoCountryName?: string;
  readonly geoStateId?: number;
  readonly geoStateName?: string;
  readonly geoLgaId?: number;
  readonly geoLgaName?: string;
  readonly geoWardId?: number;
  readonly geoWardName?: string;
}

export interface ApplyResponse {
  readonly franchise: Franchise;
  readonly application: FranchiseApplication;
}

export async function applyForFranchise(dto: ApplyFranchiseDto): Promise<ApplyResponse> {
  return request<ApplyResponse>(baseUrl(), '/v1/franchise/franchises/apply', {
    method: 'POST',
    body: dto,
  });
}

export async function submitFranchiseApplication(franchiseId: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/submit`,
    { method: 'POST', body: {} },
  );
}

export async function reviewApplication(
  applicationId: string,
  decision: 'APPROVE' | 'REJECT',
  note: string,
): Promise<FranchiseApplication> {
  return request<FranchiseApplication>(
    baseUrl(),
    `/v1/franchise/franchises/applications/${encodeURIComponent(applicationId)}/review`,
    { method: 'POST', body: { decision, note } },
  );
}

export async function provisionDevice(
  franchiseId: string,
  deviceProfile: Record<string, unknown>,
): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/provision-device`,
    { method: 'POST', body: { deviceProfile } },
  );
}

export async function recordTraining(
  franchiseId: string,
  certificateRef: string,
): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/record-training`,
    { method: 'POST', body: { certificateRef } },
  );
}

export async function activateFranchise(franchiseId: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/activate`,
    { method: 'POST', body: {} },
  );
}

export async function suspendFranchise(franchiseId: string, reason: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/suspend`,
    { method: 'POST', body: { reason } },
  );
}

export async function unsuspendFranchise(franchiseId: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/unsuspend`,
    { method: 'POST', body: {} },
  );
}

export async function terminateFranchise(franchiseId: string, reason: string): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/franchises/${encodeURIComponent(franchiseId)}/terminate`,
    { method: 'POST', body: { reason } },
  );
}

// ─── Agreements ───────────────────────────────────────────────────────────

export interface CreateAgreementDto {
  readonly franchiseId: string;
  readonly terms: Record<string, unknown>;
  readonly effectiveFrom: string;
  readonly effectiveUntil: string;
}

export async function createAgreement(dto: CreateAgreementDto): Promise<FranchiseAgreement> {
  return request<FranchiseAgreement>(baseUrl(), '/v1/franchise/agreements', {
    method: 'POST',
    body: dto,
  });
}

export async function signAgreement(agreementId: string): Promise<FranchiseAgreement> {
  return request<FranchiseAgreement>(
    baseUrl(),
    `/v1/franchise/agreements/${encodeURIComponent(agreementId)}/sign`,
    { method: 'POST', body: {} },
  );
}

export async function listAgreementsForFranchise(
  franchiseId: string,
): Promise<readonly FranchiseAgreement[]> {
  return request<readonly FranchiseAgreement[]>(
    baseUrl(),
    `/v1/franchise/agreements/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function getAgreement(agreementId: string): Promise<FranchiseAgreement> {
  return request<FranchiseAgreement>(
    baseUrl(),
    `/v1/franchise/agreements/${encodeURIComponent(agreementId)}`,
    { method: 'GET' },
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────

export async function getCentralMap(): Promise<CentralMapView> {
  return request<CentralMapView>(baseUrl(), '/v1/franchise/dashboard/central/map', {
    method: 'GET',
  });
}

export async function getCentralRevenue(query: {
  readonly fromDate: string;
  readonly toDate: string;
}): Promise<CentralRevenueDashboard> {
  return request<CentralRevenueDashboard>(baseUrl(), '/v1/franchise/dashboard/central/revenue', {
    method: 'GET',
    query: { fromDate: query.fromDate, toDate: query.toDate },
  });
}

export async function getAlerts(): Promise<AlertsAnomaliesResponse> {
  return request<AlertsAnomaliesResponse>(baseUrl(), '/v1/franchise/dashboard/alerts', {
    method: 'GET',
  });
}

export async function getFranchiseeDashboard(franchiseId: string): Promise<FranchiseeDashboard> {
  return request<FranchiseeDashboard>(
    baseUrl(),
    `/v1/franchise/dashboard/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

// ─── Revenue snapshots ────────────────────────────────────────────────────

export type RevenueSnapshotStatus = 'PENDING' | 'RECONCILED' | 'DISPUTED' | 'SETTLED';

export interface RevenueSnapshot {
  readonly id: string;
  readonly franchiseId: string;
  readonly date: string;
  readonly totalRevenue: string;
  readonly revenueByService: unknown;
  readonly txnCount: number;
  readonly commissionAmount: string;
  readonly status: RevenueSnapshotStatus;
  readonly reconciledRef: string | null;
  readonly computedAt: string;
  readonly reconciledAt: string | null;
  readonly settledAt: string | null;
}

export interface ListRevenueSnapshotsResponse {
  readonly items: readonly RevenueSnapshot[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface AggregateRevenueResponse {
  readonly franchiseId: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly totalRevenue: string;
  readonly totalCommission: string;
  readonly descendantCount: number;
  readonly snapshotCount: number;
}

export interface ListSnapshotsQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly status?: RevenueSnapshotStatus;
}

export async function listRevenueSnapshots(
  franchiseId: string,
  query: ListSnapshotsQuery = {},
): Promise<ListRevenueSnapshotsResponse> {
  return request<ListRevenueSnapshotsResponse>(
    baseUrl(),
    `/v1/franchise/revenue/franchise/${encodeURIComponent(franchiseId)}/snapshots`,
    {
      method: 'GET',
      query: {
        page: query.page,
        pageSize: query.pageSize,
        fromDate: query.fromDate,
        toDate: query.toDate,
        status: query.status,
      },
    },
  );
}

export async function aggregateRevenue(
  franchiseId: string,
  query: { readonly fromDate: string; readonly toDate: string },
): Promise<AggregateRevenueResponse> {
  return request<AggregateRevenueResponse>(
    baseUrl(),
    `/v1/franchise/revenue/franchise/${encodeURIComponent(franchiseId)}/aggregate`,
    {
      method: 'GET',
      query: { fromDate: query.fromDate, toDate: query.toDate },
    },
  );
}

export async function reconcileSnapshot(
  snapshotId: string,
  ledgerRef: string,
): Promise<RevenueSnapshot> {
  return request<RevenueSnapshot>(
    baseUrl(),
    `/v1/franchise/revenue/snapshots/${encodeURIComponent(snapshotId)}/reconcile`,
    { method: 'POST', body: { ledgerRef } },
  );
}

export async function disputeSnapshot(
  snapshotId: string,
  note: string,
): Promise<RevenueSnapshot> {
  return request<RevenueSnapshot>(
    baseUrl(),
    `/v1/franchise/revenue/snapshots/${encodeURIComponent(snapshotId)}/dispute`,
    { method: 'POST', body: { note } },
  );
}

export async function settleSnapshot(snapshotId: string): Promise<RevenueSnapshot> {
  return request<RevenueSnapshot>(
    baseUrl(),
    `/v1/franchise/revenue/snapshots/${encodeURIComponent(snapshotId)}/settle`,
    { method: 'POST', body: {} },
  );
}

// ─── Settlement runs ──────────────────────────────────────────────────────

export type SettlementRunStatus = 'PENDING' | 'INITIATED' | 'COMPLETED' | 'FAILED';

export interface SettlementRun {
  readonly id: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly franchiseId: string;
  readonly totalRevenue: string;
  readonly totalCommission: string;
  readonly netRemitted: string;
  readonly status: SettlementRunStatus;
  readonly ledgerJournalId: string | null;
  readonly failureReason: string | null;
  readonly initiatedBy: string;
  readonly initiatedAt: string;
  readonly completedAt: string | null;
}

export interface ListSettlementsResponse {
  readonly items: readonly SettlementRun[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface RunDailySettlementResponse {
  readonly runs: readonly SettlementRun[];
  readonly skipped: number;
}

export interface ListSettlementsQuery {
  readonly page?: number;
  readonly pageSize?: number;
  readonly fromDate?: string;
  readonly toDate?: string;
  readonly status?: SettlementRunStatus;
}

export async function listSettlementsForFranchise(
  franchiseId: string,
  query: ListSettlementsQuery = {},
): Promise<ListSettlementsResponse> {
  return request<ListSettlementsResponse>(
    baseUrl(),
    `/v1/franchise/settlements/franchise/${encodeURIComponent(franchiseId)}`,
    {
      method: 'GET',
      query: {
        page: query.page,
        pageSize: query.pageSize,
        fromDate: query.fromDate,
        toDate: query.toDate,
        status: query.status,
      },
    },
  );
}

export async function getSettlement(id: string): Promise<SettlementRun> {
  return request<SettlementRun>(
    baseUrl(),
    `/v1/franchise/settlements/${encodeURIComponent(id)}`,
    { method: 'GET' },
  );
}

export async function runDailySettlement(date: string): Promise<RunDailySettlementResponse> {
  return request<RunDailySettlementResponse>(baseUrl(), '/v1/franchise/settlements/run', {
    method: 'POST',
    body: { date },
  });
}

// ─── Inventory ────────────────────────────────────────────────────────────

export type InventoryItemKind =
  | 'STAMP'
  | 'TRACKING_NUMBER_RANGE'
  | 'LABEL_STOCK'
  | 'RECEIPT_PAPER';

export type InventoryStatus = 'ALLOCATED' | 'LOW' | 'DEPLETED' | 'RETURNED';

export interface InventoryAllocation {
  readonly id: string;
  readonly franchiseId: string;
  readonly itemKind: InventoryItemKind;
  readonly itemRef: string;
  readonly issuedQty: number;
  readonly consumedQty: number;
  readonly returnedQty: number;
  readonly status: InventoryStatus;
  readonly minThreshold: number;
  readonly issuedAt: string;
}

export interface ListInventoryAllocationsResponse {
  readonly items: readonly InventoryAllocation[];
}

export interface AllocateInventoryDto {
  readonly itemKind: InventoryItemKind;
  readonly itemRef: string;
  readonly qty: number;
  readonly minThreshold: number;
}

export async function listInventoryForFranchise(
  franchiseId: string,
): Promise<ListInventoryAllocationsResponse> {
  return request<ListInventoryAllocationsResponse>(
    baseUrl(),
    `/v1/franchise/inventory/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function allocateInventory(
  franchiseId: string,
  dto: AllocateInventoryDto,
): Promise<InventoryAllocation> {
  return request<InventoryAllocation>(
    baseUrl(),
    `/v1/franchise/inventory/franchise/${encodeURIComponent(franchiseId)}/allocate`,
    { method: 'POST', body: dto },
  );
}

export async function consumeInventory(
  allocationId: string,
  qty: number,
): Promise<InventoryAllocation> {
  return request<InventoryAllocation>(
    baseUrl(),
    `/v1/franchise/inventory/${encodeURIComponent(allocationId)}/consume`,
    { method: 'POST', body: { qty } },
  );
}

export async function returnInventory(
  allocationId: string,
  qty: number,
  reason: string,
): Promise<InventoryAllocation> {
  return request<InventoryAllocation>(
    baseUrl(),
    `/v1/franchise/inventory/${encodeURIComponent(allocationId)}/return`,
    { method: 'POST', body: { qty, reason } },
  );
}

export async function replenishInventory(
  allocationId: string,
  qty: number,
): Promise<InventoryAllocation> {
  return request<InventoryAllocation>(
    baseUrl(),
    `/v1/franchise/inventory/${encodeURIComponent(allocationId)}/replenish`,
    { method: 'POST', body: { qty } },
  );
}

// ─── Bulletins / broadcasts ───────────────────────────────────────────────

export type BulletinPriority = 'INFO' | 'WARN' | 'URGENT';

export interface FranchiseBulletin {
  readonly id: string;
  readonly subject: string;
  readonly body: string;
  readonly priority: BulletinPriority;
  readonly targetTiers: unknown;
  readonly targetFranchiseIds: unknown;
  readonly publishedBy: string;
  readonly publishedAt: string;
  readonly expiresAt: string | null;
}

export interface ListBulletinsResponse {
  readonly items: readonly FranchiseBulletin[];
}

export interface PublishBulletinDto {
  readonly subject: string;
  readonly body: string;
  readonly priority: BulletinPriority;
  readonly targetTiers?: readonly TierCode[];
  readonly targetFranchiseIds?: readonly string[];
  readonly expiresAt?: string;
}

export async function listAllBulletins(): Promise<ListBulletinsResponse> {
  return request<ListBulletinsResponse>(baseUrl(), '/v1/franchise/bulletins', { method: 'GET' });
}

export async function listBulletinsForFranchise(
  franchiseId: string,
): Promise<ListBulletinsResponse> {
  return request<ListBulletinsResponse>(
    baseUrl(),
    `/v1/franchise/bulletins/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function publishBulletin(dto: PublishBulletinDto): Promise<FranchiseBulletin> {
  return request<FranchiseBulletin>(baseUrl(), '/v1/franchise/bulletins', {
    method: 'POST',
    body: dto,
  });
}

export async function acknowledgeBulletin(
  bulletinId: string,
  franchiseId: string,
): Promise<{ readonly ok: true; readonly bulletinId: string; readonly franchiseId: string }> {
  return request(
    baseUrl(),
    `/v1/franchise/bulletins/${encodeURIComponent(bulletinId)}/franchise/${encodeURIComponent(franchiseId)}/acknowledge`,
    { method: 'POST', body: {} },
  );
}

// ─── Onboarding extensions: applications list ─────────────────────────────

export interface FranchiseApplicationListItem extends FranchiseApplication {
  readonly franchise: Franchise | null;
}

export interface ListApplicationsResponse {
  readonly items: readonly FranchiseApplicationListItem[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListApplicationsQuery {
  readonly status?: ApplicationStatus;
  readonly franchiseId?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listAllApplications(
  query: ListApplicationsQuery = {},
): Promise<ListApplicationsResponse> {
  return request<ListApplicationsResponse>(baseUrl(), '/v1/franchise/franchises/applications', {
    method: 'GET',
    query: {
      status: query.status,
      franchiseId: query.franchiseId,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

// ─── Onboarding extensions: devices ───────────────────────────────────────

export type DeviceStatus = 'ACTIVE' | 'DECOMMISSIONED' | 'MAINTENANCE' | 'LOST';

export interface FranchiseDevice {
  readonly id: string;
  readonly franchiseId: string;
  readonly serial: string;
  readonly model: string | null;
  readonly posTerminalId: string | null;
  readonly status: DeviceStatus;
  readonly provisionedBy: string;
  readonly provisionedAt: string;
  readonly lastSeenAt: string | null;
  readonly decommissionedAt: string | null;
}

export interface ListDevicesResponse {
  readonly items: readonly FranchiseDevice[];
  // eslint-disable-next-line upmp/no-number-money -- row count.
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface ListDevicesQuery {
  readonly franchiseId?: string;
  readonly status?: DeviceStatus;
  readonly page?: number;
  readonly pageSize?: number;
}

export async function listDevices(
  query: ListDevicesQuery = {},
): Promise<ListDevicesResponse> {
  return request<ListDevicesResponse>(baseUrl(), '/v1/franchise/devices', {
    method: 'GET',
    query: {
      franchiseId: query.franchiseId,
      status: query.status,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function listDevicesForFranchise(franchiseId: string): Promise<readonly FranchiseDevice[]> {
  return request<readonly FranchiseDevice[]>(
    baseUrl(),
    `/v1/franchise/devices/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export interface ProvisionDeviceDto {
  readonly franchiseId: string;
  readonly serial: string;
  readonly model?: string;
  readonly posTerminalId?: string;
}

export async function createDevice(dto: ProvisionDeviceDto): Promise<FranchiseDevice> {
  return request<FranchiseDevice>(baseUrl(), '/v1/franchise/devices', {
    method: 'POST',
    body: dto,
  });
}

export async function decommissionDevice(deviceId: string): Promise<FranchiseDevice> {
  return request<FranchiseDevice>(
    baseUrl(),
    `/v1/franchise/devices/${encodeURIComponent(deviceId)}/decommission`,
    { method: 'POST', body: {} },
  );
}

export async function heartbeatDevice(deviceId: string): Promise<FranchiseDevice> {
  return request<FranchiseDevice>(
    baseUrl(),
    `/v1/franchise/devices/${encodeURIComponent(deviceId)}/heartbeat`,
    { method: 'POST', body: {} },
  );
}

// ─── Onboarding extensions: certifications ────────────────────────────────

export type CertStatus = 'ISSUED' | 'EXPIRED' | 'REVOKED';

export interface FranchiseCertification {
  readonly id: string;
  readonly franchiseId: string;
  readonly certKind: string;
  readonly certificateRef: string;
  readonly status: CertStatus;
  readonly issuedBy: string;
  readonly issuedAt: string;
  readonly expiresAt: string | null;
  readonly revokedAt: string | null;
  readonly revokeReason: string | null;
}

export interface IssueCertificationDto {
  readonly franchiseId: string;
  readonly certKind?: string;
  readonly certificateRef: string;
  readonly expiresAt?: string;
}

export interface ListCertificationsQuery {
  readonly franchiseId?: string;
  readonly status?: CertStatus;
  readonly certKind?: string;
}

export async function listCertifications(
  query: ListCertificationsQuery = {},
): Promise<readonly FranchiseCertification[]> {
  return request<readonly FranchiseCertification[]>(baseUrl(), '/v1/franchise/certifications', {
    method: 'GET',
    query: {
      franchiseId: query.franchiseId,
      status: query.status,
      certKind: query.certKind,
    },
  });
}

export async function listCertificationsForFranchise(
  franchiseId: string,
): Promise<readonly FranchiseCertification[]> {
  return request<readonly FranchiseCertification[]>(
    baseUrl(),
    `/v1/franchise/certifications/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function issueCertification(
  dto: IssueCertificationDto,
): Promise<FranchiseCertification> {
  return request<FranchiseCertification>(baseUrl(), '/v1/franchise/certifications', {
    method: 'POST',
    body: dto,
  });
}

export async function revokeCertification(
  certId: string,
  reason: string,
): Promise<FranchiseCertification> {
  return request<FranchiseCertification>(
    baseUrl(),
    `/v1/franchise/certifications/${encodeURIComponent(certId)}/revoke`,
    { method: 'POST', body: { reason } },
  );
}

// ─── Onboarding extensions: training records ──────────────────────────────

export type TrainingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface FranchiseTrainingRecord {
  readonly id: string;
  readonly franchiseId: string;
  readonly moduleCode: string;
  readonly moduleName: string;
  readonly status: TrainingStatus;
  readonly progressPercent: number;
  readonly score: number | null;
  readonly instructorId: string | null;
  readonly recordedBy: string;
  readonly startedAt: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface UpsertTrainingRecordDto {
  readonly franchiseId: string;
  readonly moduleCode: string;
  readonly moduleName: string;
  readonly status: TrainingStatus;
  readonly progressPercent: number;
  readonly score?: number;
  readonly instructorId?: string;
}

export interface ListTrainingQuery {
  readonly franchiseId?: string;
  readonly status?: TrainingStatus;
}

export async function listTrainingRecords(
  query: ListTrainingQuery = {},
): Promise<readonly FranchiseTrainingRecord[]> {
  return request<readonly FranchiseTrainingRecord[]>(baseUrl(), '/v1/franchise/training', {
    method: 'GET',
    query: { franchiseId: query.franchiseId, status: query.status },
  });
}

export async function listTrainingForFranchise(
  franchiseId: string,
): Promise<readonly FranchiseTrainingRecord[]> {
  return request<readonly FranchiseTrainingRecord[]>(
    baseUrl(),
    `/v1/franchise/training/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function upsertTrainingRecord(
  dto: UpsertTrainingRecordDto,
): Promise<FranchiseTrainingRecord> {
  return request<FranchiseTrainingRecord>(baseUrl(), '/v1/franchise/training', {
    method: 'POST',
    body: dto,
  });
}

// ─── Onboarding extensions: service catalogue ─────────────────────────────

export interface ServiceCatalogueEntry {
  readonly id: string;
  readonly serviceCode: string;
  readonly displayName: string;
  readonly description: string;
  readonly category: string;
  readonly eligibleTiers: readonly TierCode[];
  readonly fee: string;
  readonly rules: unknown;
  readonly active: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListServiceCatalogueResponse {
  readonly items: readonly ServiceCatalogueEntry[];
}

export interface ListServiceCatalogueQuery {
  readonly active?: boolean;
  readonly category?: string;
  readonly tier?: TierCode;
}

export async function listServiceCatalogue(
  query: ListServiceCatalogueQuery = {},
): Promise<ListServiceCatalogueResponse> {
  return request<ListServiceCatalogueResponse>(baseUrl(), '/v1/franchise/service-catalogue', {
    method: 'GET',
    query: { active: query.active, category: query.category, tier: query.tier },
  });
}

// ─── Onboarding extensions: tier changes ──────────────────────────────────

export type TierChangeDirection = 'UPGRADE' | 'DOWNGRADE';
export type TierChangeStatus = 'REQUESTED' | 'APPROVED' | 'APPLIED' | 'REJECTED';

export interface FranchiseTierChange {
  readonly id: string;
  readonly franchiseId: string;
  readonly fromTier: TierCode;
  readonly toTier: TierCode;
  readonly direction: TierChangeDirection;
  readonly status: TierChangeStatus;
  readonly reason: string;
  readonly effectiveDate: string | null;
  readonly requestedBy: string;
  readonly requestedAt: string;
  readonly decidedBy: string | null;
  readonly decidedAt: string | null;
  readonly decisionNote: string | null;
  readonly appliedAt: string | null;
}

export interface RequestTierChangeDto {
  readonly toTier: TierCode;
  readonly reason: string;
  readonly effectiveDate?: string;
}

export interface ListTierChangesQuery {
  readonly franchiseId?: string;
  readonly status?: TierChangeStatus;
}

export async function listTierChanges(
  query: ListTierChangesQuery = {},
): Promise<readonly FranchiseTierChange[]> {
  return request<readonly FranchiseTierChange[]>(baseUrl(), '/v1/franchise/tier-changes', {
    method: 'GET',
    query: { franchiseId: query.franchiseId, status: query.status },
  });
}

export async function requestTierChange(
  franchiseId: string,
  dto: RequestTierChangeDto,
): Promise<FranchiseTierChange> {
  return request<FranchiseTierChange>(
    baseUrl(),
    `/v1/franchise/tier-changes/franchise/${encodeURIComponent(franchiseId)}/request`,
    { method: 'POST', body: dto },
  );
}

export async function approveTierChange(id: string, note: string): Promise<FranchiseTierChange> {
  return request<FranchiseTierChange>(
    baseUrl(),
    `/v1/franchise/tier-changes/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: { note } },
  );
}

export async function rejectTierChange(id: string, note: string): Promise<FranchiseTierChange> {
  return request<FranchiseTierChange>(
    baseUrl(),
    `/v1/franchise/tier-changes/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: { note } },
  );
}

export async function applyTierChange(id: string): Promise<FranchiseTierChange> {
  return request<FranchiseTierChange>(
    baseUrl(),
    `/v1/franchise/tier-changes/${encodeURIComponent(id)}/apply`,
    { method: 'POST', body: {} },
  );
}

// ─── Onboarding extensions: tier admin update ─────────────────────────────

export interface UpdateTierDto {
  readonly displayName?: string;
  readonly description?: string;
  readonly dailyTxnLimit?: string;
  readonly allowedServices?: readonly string[];
  readonly defaultPermissions?: readonly string[];
  readonly deviceProfile?: Record<string, unknown>;
  readonly active?: boolean;
}

export async function updateTier(code: string, dto: UpdateTierDto): Promise<FranchiseTier> {
  return request<FranchiseTier>(
    baseUrl(),
    `/v1/franchise/tiers/${encodeURIComponent(code)}`,
    { method: 'PATCH', body: dto },
  );
}

// ─── Onboarding extensions: agreement counter-signature + PDF ─────────────

export async function counterSignAgreement(agreementId: string): Promise<FranchiseAgreement> {
  return request<FranchiseAgreement>(
    baseUrl(),
    `/v1/franchise/agreements/${encodeURIComponent(agreementId)}/counter-sign`,
    { method: 'POST', body: {} },
  );
}

export async function setAgreementPdfUrl(
  agreementId: string,
  pdfUrl: string,
): Promise<FranchiseAgreement> {
  return request<FranchiseAgreement>(
    baseUrl(),
    `/v1/franchise/agreements/${encodeURIComponent(agreementId)}/pdf`,
    { method: 'POST', body: { pdfUrl } },
  );
}

// ─── Operations slice 4b (deferred-notice removal) ──────────────────────────
// All endpoints below mount under /v1/franchise/ops/* on service-franchise
// and back the staff console operations pages (broadcast acks, discrepancy
// engine, performance KPIs, transaction event log, incentive rules / grants,
// remittance schedule, national-map pin coords, suspension request queue).

export type SuspensionRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface SuspensionRequest {
  readonly id: string;
  readonly franchiseId: string;
  readonly reason: string;
  readonly requestedBy: string;
  readonly status: SuspensionRequestStatus;
  readonly reviewerId: string | null;
  readonly reviewerNote: string | null;
  readonly decidedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListSuspensionRequestsResponse {
  readonly items: readonly SuspensionRequest[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
}

export async function createSuspensionRequest(input: {
  franchiseId: string;
  reason: string;
}): Promise<SuspensionRequest> {
  return request<SuspensionRequest>(baseUrl(), '/v1/franchise/ops/suspension-requests', {
    method: 'POST',
    body: input,
  });
}

export async function listSuspensionRequests(
  query: { status?: SuspensionRequestStatus; franchiseId?: string; pageSize?: number } = {},
): Promise<ListSuspensionRequestsResponse> {
  return request<ListSuspensionRequestsResponse>(
    baseUrl(),
    '/v1/franchise/ops/suspension-requests',
    {
      method: 'GET',
      query: { status: query.status, franchiseId: query.franchiseId, pageSize: query.pageSize },
    },
  );
}

export async function reviewSuspensionRequest(
  id: string,
  decision: 'APPROVE' | 'REJECT',
  note: string,
): Promise<SuspensionRequest> {
  return request<SuspensionRequest>(
    baseUrl(),
    `/v1/franchise/ops/suspension-requests/${encodeURIComponent(id)}/review`,
    { method: 'POST', body: { decision, note } },
  );
}

export type DiscrepancyKind =
  | 'REVENUE_VARIANCE'
  | 'INVENTORY_VARIANCE'
  | 'SETTLEMENT_VARIANCE'
  | 'OTHER';

export type DiscrepancySeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type DiscrepancyStatus = 'OPEN' | 'INVESTIGATING' | 'RESOLVED' | 'CLOSED';

export interface Discrepancy {
  readonly id: string;
  readonly franchiseId: string;
  readonly kind: DiscrepancyKind;
  readonly severity: DiscrepancySeverity;
  readonly status: DiscrepancyStatus;
  readonly snapshotId: string | null;
  readonly expectedAmount: string;
  readonly actualAmount: string;
  readonly variance: string;
  readonly description: string;
  readonly assigneeId: string | null;
  readonly slaHours: number;
  readonly detectedBy: string;
  readonly detectedAt: string;
  readonly resolvedBy: string | null;
  readonly resolvedAt: string | null;
  readonly resolutionNote: string | null;
}

export interface ListDiscrepanciesResponse {
  readonly items: readonly Discrepancy[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
   
  readonly openCount: number;
   
  readonly investigatingCount: number;
   
  readonly resolvedCount: number;
}

export async function createDiscrepancy(input: {
  franchiseId: string;
  kind?: DiscrepancyKind;
  severity?: DiscrepancySeverity;
  snapshotId?: string;
  expectedAmount?: string;
  actualAmount?: string;
  description: string;
  assigneeId?: string;
  slaHours?: number;
}): Promise<Discrepancy> {
  return request<Discrepancy>(baseUrl(), '/v1/franchise/ops/discrepancies', {
    method: 'POST',
    body: input,
  });
}

export async function listDiscrepancies(
  query: {
    status?: DiscrepancyStatus;
    severity?: DiscrepancySeverity;
    franchiseId?: string;
    pageSize?: number;
  } = {},
): Promise<ListDiscrepanciesResponse> {
  return request<ListDiscrepanciesResponse>(baseUrl(), '/v1/franchise/ops/discrepancies', {
    method: 'GET',
    query: {
      status: query.status,
      severity: query.severity,
      franchiseId: query.franchiseId,
      pageSize: query.pageSize,
    },
  });
}

export async function updateDiscrepancyStatus(
  id: string,
  input: { status: DiscrepancyStatus; assigneeId?: string; resolutionNote?: string },
): Promise<Discrepancy> {
  return request<Discrepancy>(
    baseUrl(),
    `/v1/franchise/ops/discrepancies/${encodeURIComponent(id)}/status`,
    { method: 'POST', body: input },
  );
}

export interface PerformanceKpi {
  readonly id: string;
  readonly franchiseId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly slaOnTimeRate: string;
  readonly csatScore: string;
   
  readonly slaBreachCount: number;
   
  readonly complaintCount: number;
  readonly note: string | null;
  readonly recordedBy: string;
  readonly recordedAt: string;
}

export interface ListPerformanceKpisResponse {
  readonly items: readonly PerformanceKpi[];
  readonly latest: PerformanceKpi | null;
}

export async function recordPerformanceKpi(input: {
  franchiseId: string;
  periodStart: string;
  periodEnd: string;
  slaOnTimeRate: string;
  csatScore: string;
  slaBreachCount?: number;
  complaintCount?: number;
  note?: string;
}): Promise<PerformanceKpi> {
  return request<PerformanceKpi>(baseUrl(), '/v1/franchise/ops/performance-kpis', {
    method: 'POST',
    body: input,
  });
}

export async function listPerformanceKpis(
  franchiseId: string,
  query: { pageSize?: number } = {},
): Promise<ListPerformanceKpisResponse> {
  return request<ListPerformanceKpisResponse>(
    baseUrl(),
    `/v1/franchise/ops/performance-kpis/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET', query: { pageSize: query.pageSize } },
  );
}

export interface TransactionEvent {
  readonly id: string;
  readonly franchiseId: string;
  readonly sourceService: string;
  readonly eventType: string;
  readonly referenceId: string;
  readonly amount: string;
  readonly description: string;
  readonly payload: unknown;
  readonly occurredAt: string;
  readonly recordedAt: string;
}

export interface ListTransactionEventsResponse {
  readonly items: readonly TransactionEvent[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
   
  readonly page: number;
   
  readonly pageSize: number;
  readonly totalAmount: string;
}

export async function recordTransactionEvent(input: {
  franchiseId: string;
  sourceService: string;
  eventType: string;
  referenceId: string;
  amount?: string;
  description: string;
  payload?: Record<string, unknown>;
  occurredAt?: string;
}): Promise<TransactionEvent> {
  return request<TransactionEvent>(baseUrl(), '/v1/franchise/ops/transaction-events', {
    method: 'POST',
    body: input,
  });
}

export async function listTransactionEvents(query: {
  franchiseId: string;
  sourceService?: string;
  eventType?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}): Promise<ListTransactionEventsResponse> {
  return request<ListTransactionEventsResponse>(baseUrl(), '/v1/franchise/ops/transaction-events', {
    method: 'GET',
    query: {
      franchiseId: query.franchiseId,
      sourceService: query.sourceService,
      eventType: query.eventType,
      fromDate: query.fromDate,
      toDate: query.toDate,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export interface IncentiveRule {
  readonly id: string;
  readonly tierCode: TierCode;
  readonly name: string;
  readonly revenueThreshold: string;
  readonly bonusMultiplier: string;
  readonly flatBonus: string;
  readonly active: boolean;
  readonly createdBy: string;
  readonly createdAt: string;
}

export interface ListIncentiveRulesResponse {
  readonly items: readonly IncentiveRule[];
}

export async function createIncentiveRule(input: {
  tierCode: TierCode;
  name: string;
  revenueThreshold: string;
  bonusMultiplier: string;
  flatBonus?: string;
  active?: boolean;
}): Promise<IncentiveRule> {
  return request<IncentiveRule>(baseUrl(), '/v1/franchise/ops/incentive-rules', {
    method: 'POST',
    body: input,
  });
}

export async function listIncentiveRules(
  query: { tierCode?: TierCode; active?: boolean } = {},
): Promise<ListIncentiveRulesResponse> {
  return request<ListIncentiveRulesResponse>(baseUrl(), '/v1/franchise/ops/incentive-rules', {
    method: 'GET',
    query: { tierCode: query.tierCode, active: query.active },
  });
}

export interface IncentiveGrant {
  readonly id: string;
  readonly franchiseId: string;
  readonly ruleId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly baseCommission: string;
  readonly bonusAmount: string;
  readonly totalIncentive: string;
  readonly status: 'PENDING' | 'APPROVED' | 'PAID';
  readonly computedAt: string;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
}

export interface ListIncentiveGrantsResponse {
  readonly items: readonly IncentiveGrant[];
  readonly totalGranted: string;
}

export async function computeIncentiveGrant(input: {
  franchiseId: string;
  periodStart: string;
  periodEnd: string;
}): Promise<ListIncentiveGrantsResponse> {
  return request<ListIncentiveGrantsResponse>(baseUrl(), '/v1/franchise/ops/incentive-grants/compute', {
    method: 'POST',
    body: input,
  });
}

export async function listIncentiveGrants(
  franchiseId: string,
): Promise<ListIncentiveGrantsResponse> {
  return request<ListIncentiveGrantsResponse>(
    baseUrl(),
    `/v1/franchise/ops/incentive-grants/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function approveIncentiveGrant(id: string): Promise<IncentiveGrant> {
  return request<IncentiveGrant>(
    baseUrl(),
    `/v1/franchise/ops/incentive-grants/${encodeURIComponent(id)}/approve`,
    { method: 'POST', body: {} },
  );
}

export type RemittanceCadence = 'DAILY' | 'WEEKLY' | 'BIWEEKLY' | 'MONTHLY';

export interface RemittanceSchedule {
  readonly id: string;
  readonly franchiseId: string;
  readonly cadence: RemittanceCadence;
   
  readonly dayOfPeriod: number;
  readonly timeOfDay: string;
  readonly skipWeekends: boolean;
   
  readonly slaHours: number;
  readonly active: boolean;
  readonly nextRunAt: string | null;
  readonly lastRunAt: string | null;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ListRemittanceSchedulesResponse {
  readonly items: readonly RemittanceSchedule[];
}

export async function setRemittanceSchedule(
  franchiseId: string,
  input: {
    cadence: RemittanceCadence;
    dayOfPeriod?: number;
    timeOfDay?: string;
    skipWeekends?: boolean;
    slaHours?: number;
    active?: boolean;
  },
): Promise<RemittanceSchedule> {
  return request<RemittanceSchedule>(
    baseUrl(),
    `/v1/franchise/ops/remittance-schedule/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'PUT', body: input },
  );
}

export async function getRemittanceSchedule(
  franchiseId: string,
): Promise<RemittanceSchedule | null> {
  return request<RemittanceSchedule | null>(
    baseUrl(),
    `/v1/franchise/ops/remittance-schedule/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'GET' },
  );
}

export async function listRemittanceSchedules(): Promise<ListRemittanceSchedulesResponse> {
  return request<ListRemittanceSchedulesResponse>(baseUrl(), '/v1/franchise/ops/remittance-schedule', {
    method: 'GET',
  });
}

export interface FranchisePin {
  readonly franchiseId: string;
  readonly franchiseNumber: string;
  readonly legalName: string;
  readonly state: string;
  readonly lga: string;
  readonly tierCode: TierCode;
  readonly status: FranchiseStatus;
  readonly latitude: string;
  readonly longitude: string;
}

export interface ListFranchisePinsResponse {
  readonly items: readonly FranchisePin[];
}

export async function setFranchiseCoordinates(
  franchiseId: string,
  input: { latitude: string; longitude: string },
): Promise<Franchise> {
  return request<Franchise>(
    baseUrl(),
    `/v1/franchise/ops/coordinates/franchise/${encodeURIComponent(franchiseId)}`,
    { method: 'PUT', body: input },
  );
}

export async function listFranchisePins(): Promise<ListFranchisePinsResponse> {
  return request<ListFranchisePinsResponse>(baseUrl(), '/v1/franchise/ops/coordinates/pins', {
    method: 'GET',
  });
}

export interface BulletinReceiptSummary {
  readonly bulletinId: string;
  readonly subject: string;
  readonly priority: BulletinPriority;
  readonly publishedAt: string;
   
  readonly targetCount: number;
   
  readonly acknowledgedCount: number;
  readonly ackRate: string;
}

export interface ListBulletinReceiptSummariesResponse {
  readonly items: readonly BulletinReceiptSummary[];
}

export async function listBulletinReceiptSummaries(): Promise<ListBulletinReceiptSummariesResponse> {
  return request<ListBulletinReceiptSummariesResponse>(
    baseUrl(),
    '/v1/franchise/ops/bulletin-receipts',
    { method: 'GET' },
  );
}
